import { cn } from "@/lib/utils";

/**
 * Branded, layout-shaped skeletons shown while a lazy route chunk downloads
 * (and during lazyWithRetry's retry window). Three variants match the three
 * layout contexts in the app: marketing pages, admin shell, centered forms.
 *
 * Tokens used (Clean Surface v1):
 *   surface-page / surface-card / surface-muted / ink-primary / accent-primary
 *
 * Animation: Tailwind animate-pulse for block fade + a custom .animate-shimmer
 * sweep (defined in src/index.css). Both honor prefers-reduced-motion.
 */

type RouteSkeletonVariant = "marketing" | "admin" | "form" | "splash";

interface RouteSkeletonProps {
  variant?: RouteSkeletonVariant;
  className?: string;
}

/** A single shimmering block. Use for any rectangular placeholder. */
function Shimmer({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-ink-primary/10 motion-reduce:animate-none animate-pulse",
        className
      )}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/50 to-transparent motion-reduce:hidden"
      />
    </div>
  );
}

function MarketingSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav strip */}
      <div className="h-16 border-b border-border/40 bg-surface-card flex items-center px-6 gap-4">
        <Shimmer className="h-8 w-32 rounded-md" />
        <div className="flex-1" />
        <Shimmer className="h-4 w-16 rounded-full hidden md:block" />
        <Shimmer className="h-4 w-16 rounded-full hidden md:block" />
        <Shimmer className="h-4 w-16 rounded-full hidden md:block" />
        <Shimmer className="h-9 w-24 rounded-full" />
      </div>

      {/* Hero */}
      <div className="container mx-auto px-4 sm:px-6 pt-12 pb-16">
        <Shimmer className="h-[55vh] sm:h-[65vh] w-full rounded-3xl" />
      </div>

      {/* Card grid */}
      <div className="container mx-auto px-4 sm:px-6 pb-20">
        <Shimmer className="h-8 w-1/3 max-w-sm rounded-md mb-8 mx-auto" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-2xl bg-surface-card shadow-sm border border-border/40 p-6 space-y-4"
            >
              <Shimmer className="h-12 w-12 rounded-xl" />
              <Shimmer className="h-5 w-3/4 rounded-md" />
              <div className="space-y-2">
                <Shimmer className="h-3 w-full rounded-md" />
                <Shimmer className="h-3 w-11/12 rounded-md" />
                <Shimmer className="h-3 w-4/5 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminSkeleton() {
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border/40 bg-surface-card flex-col p-4 gap-3">
        <Shimmer className="h-10 w-40 rounded-md mb-4" />
        {Array.from({ length: 8 }).map((_, i) => (
          <Shimmer key={i} className="h-9 w-full rounded-full" />
        ))}
      </aside>

      {/* Main */}
      <div className="flex-1 p-6 md:p-8 space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <Shimmer className="h-8 w-48 rounded-md" />
          <div className="flex gap-2">
            <Shimmer className="h-9 w-9 rounded-full" />
            <Shimmer className="h-9 w-9 rounded-full" />
            <Shimmer className="h-9 w-32 rounded-full" />
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-surface-card border border-border/40 p-5 space-y-3"
            >
              <Shimmer className="h-4 w-24 rounded-md" />
              <Shimmer className="h-8 w-20 rounded-md" />
              <Shimmer className="h-3 w-32 rounded-md" />
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-surface-card border border-border/40 p-6 space-y-4">
          <Shimmer className="h-6 w-1/4 rounded-md" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex gap-4 items-center">
              <Shimmer className="h-10 w-10 rounded-full" />
              <Shimmer className="h-4 flex-1 rounded-md" />
              <Shimmer className="h-4 w-24 rounded-md hidden sm:block" />
              <Shimmer className="h-4 w-16 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="min-h-screen bg-background pt-24 pb-16 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-3xl bg-surface-card border border-border/40 shadow-sm p-6 sm:p-10 space-y-6">
          <Shimmer className="h-8 w-2/3 rounded-md" />
          <Shimmer className="h-4 w-full rounded-md" />
          <Shimmer className="h-4 w-5/6 rounded-md" />
          <div className="pt-4 space-y-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Shimmer className="h-3 w-24 rounded-md" />
                <Shimmer className="h-11 w-full rounded-xl" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Shimmer className="h-11 w-24 rounded-full" />
            <Shimmer className="h-11 w-32 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SplashSkeleton() {
  // Splash already has its own animated reveal; render a quiet bg so we
  // don't flash a layout skeleton before its own intro plays.
  return <div className="min-h-screen bg-background" />;
}

export function RouteSkeleton({ variant = "marketing", className }: RouteSkeletonProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn("w-full", className)}
    >
      <span className="sr-only">Loading content…</span>
      {variant === "admin" && <AdminSkeleton />}
      {variant === "form" && <FormSkeleton />}
      {variant === "splash" && <SplashSkeleton />}
      {variant === "marketing" && <MarketingSkeleton />}
    </div>
  );
}

export type { RouteSkeletonVariant };
